import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGamification } from '../../contexts/GamificationContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAward, FiX, FiStar } = FiIcons;

const BadgesDisplay = ({ showAll = false }) => {
  const { userBadges, badgeDefinitions } = useGamification();
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [showAllBadges, setShowAllBadges] = useState(showAll);

  const displayBadges = showAllBadges ? userBadges : userBadges.slice(0, 4);
  const allBadges = Object.entries(badgeDefinitions);

  const getBadgeStatus = (badgeKey) => {
    return userBadges.find(b => b.key === badgeKey) ? 'earned' : 'locked';
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Achievements</h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">
              {userBadges.length} / {allBadges.length} earned
            </span>
            <button
              onClick={() => setShowAllBadges(!showAllBadges)}
              className="text-blue-600 hover:text-blue-700 text-sm"
            >
              {showAllBadges ? 'Show Less' : 'Show All'}
            </button>
          </div>
        </div>

        {displayBadges.length === 0 ? (
          <div className="text-center py-8">
            <SafeIcon icon={FiAward} className="text-gray-400 text-4xl mx-auto mb-3" />
            <p className="text-gray-600">No badges earned yet. Start completing activities to earn badges!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {displayBadges.map((badge, index) => (
              <motion.div
                key={badge.key}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="text-center cursor-pointer group"
                onClick={() => setSelectedBadge(badge)}
              >
                <div className="bg-gradient-to-br from-yellow-400 to-orange-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform shadow-lg">
                  <span className="text-2xl">{badge.icon}</span>
                </div>
                <p className="text-xs font-medium text-gray-900">{badge.name}</p>
                <p className="text-xs text-gray-500">+{badge.points} pts</p>
              </motion.div>
            ))}
          </div>
        )}

        {userBadges.length > 4 && !showAllBadges && (
          <div className="text-center mt-4">
            <button
              onClick={() => setShowAllBadges(true)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              View all badges ({userBadges.length - 4} more)
            </button>
          </div>
        )}
      </div>

      {/* Badge Detail Modal */}
      <AnimatePresence>
        {selectedBadge && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-lg p-6 max-w-md w-full"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Badge Details</h3>
                <button
                  onClick={() => setSelectedBadge(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </button>
              </div>
              
              <div className="text-center">
                <div className="bg-gradient-to-br from-yellow-400 to-orange-500 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <span className="text-4xl">{selectedBadge.icon}</span>
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{selectedBadge.name}</h4>
                <p className="text-gray-600 mb-3">{selectedBadge.description}</p>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="flex items-center justify-center space-x-2 text-blue-700">
                    <SafeIcon icon={FiStar} />
                    <span className="font-medium">{selectedBadge.points} points earned</span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-3">
                  Earned on {new Date(selectedBadge.earnedAt).toLocaleDateString()}
                </p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default BadgesDisplay;